import React, { useState, useEffect } from 'react';
import { X, DollarSign, Calendar, Clock, MapPin, FileText, CreditCard, CheckCircle, AlertCircle, Shield, Send, Sparkles, Receipt } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import AddCardModal from './AddCardModal';

// Command Deck dark theme tokens (visual only)
const FONT_DISPLAY = "'Poppins', 'Space Grotesk', sans-serif";
const FONT_BODY = "'Work Sans', system-ui, sans-serif";
const FONT_MONO = "'JetBrains Mono', ui-monospace, monospace";

const SendOfferModal = ({ isOpen, onClose, job, applicant, onOfferSent, isDark }) => {
  const [loading, setLoading] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState('');
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [loadingCards, setLoadingCards] = useState(true);
  const [showAddCardModal, setShowAddCardModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [offerData, setOfferData] = useState(null);

  // Check if this is an advanced job with multiple crew members
  const isAdvancedJob = (job?.crewMembers && job.crewMembers.length > 1) || (job?.crew_members && job.crew_members.length > 1);
  const crewMembers = job?.crewMembers || job?.crew_members || [];

  // For advanced jobs, find the crew member matching the applicant's role
  const applicantRole = applicant?.role?.toLowerCase()?.replace(/\s+/g, '_') || '';
  const matchingCrewMember = isAdvancedJob && applicantRole
    ? crewMembers.find(m => {
        const memberRole = (m.role || '').toLowerCase().replace(/\s+/g, '_');
        return memberRole === applicantRole;
      })
    : null;

  // Use role-specific data for advanced jobs, otherwise use job-level data
  const roleHours = matchingCrewMember ? matchingCrewMember.hours : parseFloat(job?.hours || 0);
  const roleRate = matchingCrewMember ? matchingCrewMember.ratePerHour : parseFloat(job?.ratePerHour || job?.pay_rate || 0);
  const displayRole = matchingCrewMember ? matchingCrewMember.role : null;

  // Calculate financials based on role-specific or job-level data
  const jobTotal = roleRate * roleHours;
  const platformFee = jobTotal * 0.05; // 5% platform fee
  const fiftyPercent = jobTotal * 0.50;

  // Check if job is within 14 days
  const jobDate = new Date(job?.jobDate || job?.date);
  const daysUntilJob = Math.ceil((jobDate - new Date()) / (1000 * 60 * 60 * 24));
  const isWithin14Days = daysUntilJob <= 14;

  // Calculate total due on acceptance - FULL amount if within 14 days, otherwise 50% + fee
  const totalDueOnAcceptance = isWithin14Days ? (jobTotal + platformFee) : (fiftyPercent + platformFee);
  const totalDue14DaysBefore = fiftyPercent;

  // Calculate 14 days before job date
  const fourteenDaysBeforeJob = new Date(jobDate);
  fourteenDaysBeforeJob.setDate(jobDate.getDate() - 14);

  useEffect(() => {
    if (isOpen) {
      fetchPaymentMethods();

      // iOS-safe body scroll lock
      const scrollY = window.scrollY;
      document.body.style.overflow = 'hidden';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.width = '100%';
    }

    return () => {
      const scrollY = document.body.style.top;
      document.body.style.overflow = '';
      document.body.style.top = '';
      document.body.style.width = '';
      if (scrollY) {
        window.scrollTo(0, parseInt(scrollY || '0') * -1);
      }
    };
  }, [isOpen]);

  const fetchPaymentMethods = async () => {
    try {
      setLoadingCards(true);
      const user = JSON.parse(localStorage.getItem('shootzu_user') || '{}');
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/payments/methods?user_email=${user.email}`
      );
      setPaymentMethods(response.data.payment_methods || []);
      if (response.data.payment_methods?.length > 0) {
        setSelectedPaymentMethod(response.data.payment_methods[0].id);
      }
    } catch (error) {
      console.error('Error fetching payment methods:', error);
      setPaymentMethods([]);
    } finally {
      setLoadingCards(false);
    }
  };

  const handleCardAdded = () => {
    // Refresh payment methods after adding a new card
    fetchPaymentMethods();
    setShowAddCardModal(false);
    toast.success('Card added successfully!');
  };

  const handleSendOffer = async () => {
    try {
      if (!acceptedTerms) {
        toast.error('Please accept the Terms and Conditions');
        return;
      }

      if (!selectedPaymentMethod && paymentMethods.length > 0) {
        toast.error('Please select a payment method');
        return;
      }

      setLoading(true);

      const user = JSON.parse(localStorage.getItem('shootzu_user') || '{}');

      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/jobs/offer/send`,
        {
          job_id: job.id,
          applicant_email: applicant.email || applicant.applicant_email,
          applicant_id: applicant.user_id || applicant.applicant_id,
          applicant_name: applicant.full_name || applicant.applicant_name,
          pay_rate: roleRate,
          hours: roleHours,
          total_amount: jobTotal,
          platform_fee: platformFee,
          initial_payment: totalDueOnAcceptance,
          remaining_payment: isWithin14Days ? 0 : totalDue14DaysBefore,
          job_date: job.jobDate || job.date,
          payment_method_id: selectedPaymentMethod,
          is_within_14_days: isWithin14Days,
          hiring_user_email: user.email,
          // Advanced job role information
          role: displayRole || applicant?.role,
          is_advanced_job: isAdvancedJob
        }
      );

      // Store the offer data and show success modal
      setOfferData({
        ...response.data,
        applicant_name: applicant.full_name || applicant.applicant_name,
        applicant_email: applicant.email || applicant.applicant_email,
        job_title: job?.title,
        amount: jobTotal,
        role: displayRole || applicant?.role,
        is_advanced_job: isAdvancedJob
      });
      setShowSuccessModal(true);

      if (onOfferSent) {
        onOfferSent(response.data);
      }
    } catch (error) {
      console.error('Error sending offer:', error);
      toast.error(error.response?.data?.detail || 'Failed to send offer');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  const sectionCard = {
    borderRadius: 16,
    background: 'rgba(255,255,255,.04)',
    border: '1px solid rgba(255,255,255,.1)',
    overflow: 'hidden',
  };
  const sectionHeader = {
    padding: '12px 20px',
    borderBottom: '1px solid rgba(255,255,255,.08)',
  };
  const sectionLabel = {
    fontSize: 13,
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: '.6px',
    color: 'rgba(255,255,255,.45)',
    margin: 0,
  };
  const fieldLabel = {
    fontSize: 11,
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: '.4px',
    color: 'rgba(255,255,255,.45)',
  };

  return (
    <div
      className="fixed inset-0 z-[90] flex items-center justify-center"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
      style={{
        background: 'rgba(8,11,20,.66)',
        backdropFilter: 'blur(5px)',
        WebkitBackdropFilter: 'blur(5px)',
        padding: 20,
        overflow: 'hidden',
        touchAction: 'none',
        fontFamily: FONT_BODY,
      }}
    >
      <div
        className="w-[95vw] h-[85vh] lg:w-full lg:max-w-3xl lg:max-h-[90vh] flex flex-col"
        style={{
          background: '#12162A',
          border: '1px solid rgba(255,255,255,.12)',
          borderRadius: 22,
          boxShadow: '0 40px 90px -30px rgba(0,0,0,.85)',
          overflow: 'hidden',
          touchAction: 'pan-y',
        }}
      >
        {/* Header band */}
        <div
          className="flex-shrink-0"
          style={{
            position: 'relative',
            padding: '24px 26px',
            background: 'linear-gradient(120deg,#572B7C,#78287C)',
            borderRadius: '22px 22px 0 0',
            overflow: 'hidden',
          }}
        >
          <div style={{ position: 'relative', zIndex: 10 }}>
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="flex items-center gap-1.5" style={{ marginBottom: 6 }}>
                  <Sparkles className="w-3.5 h-3.5" style={{ color: 'rgba(255,255,255,.7)' }} />
                  <span style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.12em', color: 'rgba(255,255,255,.7)' }}>Job Offer</span>
                </div>
                <h2 style={{ fontFamily: FONT_DISPLAY, fontWeight: 800, fontSize: 26, color: '#fff', margin: 0, marginBottom: 10, letterSpacing: '-.4px' }}>
                  {applicant?.full_name}
                </h2>
                <div className="flex flex-wrap items-center gap-2">
                  <div style={{ padding: '6px 14px', background: 'rgba(255,255,255,.18)', borderRadius: 999 }}>
                    <span style={{ fontSize: 13, fontWeight: 500, color: '#fff' }}>
                      {job?.title}
                    </span>
                  </div>
                  {/* Show role badge for advanced jobs */}
                  {isAdvancedJob && displayRole && (
                    <div style={{ padding: '6px 14px', background: 'rgba(255,255,255,.1)', borderRadius: 999, border: '1px solid rgba(255,255,255,.25)' }}>
                      <span style={{ fontSize: 13, fontWeight: 700, color: '#fff' }}>
                        {displayRole?.replace(/_/g, ' ')}
                      </span>
                    </div>
                  )}
                </div>
              </div>
              <button
                onClick={onClose}
                aria-label="Close"
                style={{
                  flexShrink: 0,
                  width: 34,
                  height: 34,
                  borderRadius: 10,
                  background: 'rgba(255,255,255,.12)',
                  border: '1px solid rgba(255,255,255,.18)',
                  color: '#fff',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                }}
              >
                <X className="w-[18px] h-[18px]" />
              </button>
            </div>
          </div>
        </div>

        {/* Content - Scrollable */}
        <div
          className="flex-1 overflow-y-auto p-4 lg:p-8"
          style={{
            WebkitOverflowScrolling: 'touch',
            overscrollBehavior: 'contain',
            touchAction: 'pan-y'
          }}
        >
          <div className="space-y-5">
            {/* Important Notice */}
            <div style={{ padding: 16, borderRadius: 16, border: '1px solid rgba(96,165,250,.25)', background: 'rgba(96,165,250,.08)' }} className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 flex-shrink-0" style={{ color: '#60A5FA', marginTop: 2 }} />
              <p style={{ fontSize: 13, fontWeight: 500, lineHeight: 1.6, color: 'rgba(255,255,255,.85)', margin: 0 }}>
                This offer will be sent to <span style={{ fontWeight: 700, color: '#fff' }}>{applicant?.full_name}</span> and they'll need to accept before the job is confirmed.
              </p>
            </div>

            {/* Job Details - 2-Column Grid */}
            <div style={sectionCard}>
              <div style={sectionHeader}>
                <h3 style={sectionLabel}>Job Details</h3>
              </div>
              <div style={{ padding: 20 }}>
                <div className="grid grid-cols-2 gap-4">
                  {/* Job Date */}
                  <div>
                    <div className="flex items-center gap-1.5" style={{ marginBottom: 4 }}>
                      <Calendar className="w-3.5 h-3.5" style={{ color: 'rgba(255,255,255,.4)' }} />
                      <span style={fieldLabel}>Date</span>
                    </div>
                    <p style={{ fontSize: 13.5, fontWeight: 700, color: '#fff', margin: 0 }}>
                      {new Date(job?.jobDate || job?.date).toLocaleDateString('en-AU', { weekday: 'short', year: 'numeric', month: 'short', day: 'numeric' })}
                    </p>
                  </div>

                  {/* Job Location */}
                  <div>
                    <div className="flex items-center gap-1.5" style={{ marginBottom: 4 }}>
                      <MapPin className="w-3.5 h-3.5" style={{ color: 'rgba(255,255,255,.4)' }} />
                      <span style={fieldLabel}>Location</span>
                    </div>
                    <p className="truncate" style={{ fontSize: 13.5, fontWeight: 700, color: '#fff', margin: 0 }}>
                      {job?.locationAddress || job?.location_address || job?.location}
                    </p>
                  </div>

                  {/* Pay Rate */}
                  <div>
                    <div className="flex items-center gap-1.5" style={{ marginBottom: 4 }}>
                      <DollarSign className="w-3.5 h-3.5" style={{ color: 'rgba(255,255,255,.4)' }} />
                      <span style={fieldLabel}>Pay Rate</span>
                    </div>
                    <p style={{ fontSize: 13.5, fontWeight: 700, color: '#fff', margin: 0 }}>
                      ${roleRate.toFixed(2)}/hr
                    </p>
                  </div>

                  {/* Hours */}
                  <div>
                    <div className="flex items-center gap-1.5" style={{ marginBottom: 4 }}>
                      <Clock className="w-3.5 h-3.5" style={{ color: 'rgba(255,255,255,.4)' }} />
                      <span style={fieldLabel}>Hours</span>
                    </div>
                    <p style={{ fontSize: 13.5, fontWeight: 700, color: '#fff', margin: 0 }}>
                      {roleHours} hours
                    </p>
                  </div>
                </div>

                {/* Job Brief */}
                {job?.description && (
                  <>
                    <div style={{ margin: '16px 0', borderTop: '1px solid rgba(255,255,255,.08)' }}></div>
                    <div>
                      <div className="flex items-center gap-1.5" style={{ marginBottom: 6 }}>
                        <FileText className="w-3.5 h-3.5" style={{ color: 'rgba(255,255,255,.4)' }} />
                        <span style={fieldLabel}>Job Brief</span>
                      </div>
                      <p className="whitespace-pre-wrap line-clamp-3" style={{ fontSize: 13.5, lineHeight: 1.6, color: 'rgba(255,255,255,.75)', margin: 0 }}>
                        {job.description}
                      </p>
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* Financial Breakdown */}
            <div style={{ ...sectionCard, border: '1px solid rgba(103,198,180,.22)' }}>
              <div style={{ padding: '12px 20px', borderBottom: '1px solid rgba(103,198,180,.2)' }}>
                <div className="flex items-center gap-2">
                  <Receipt className="w-3.5 h-3.5" style={{ color: '#67C6B4' }} />
                  <h3 style={sectionLabel}>Payment Breakdown</h3>
                </div>
              </div>
              <div style={{ padding: 20 }} className="space-y-3">
                {/* Job Total */}
                <div className="flex items-center justify-between">
                  <span style={{ fontSize: 13.5, fontWeight: 500, color: 'rgba(255,255,255,.6)' }}>
                    Job Total ({roleHours}h x ${roleRate.toFixed(2)})
                  </span>
                  <span style={{ fontSize: 17, fontWeight: 700, color: '#fff', fontFamily: FONT_DISPLAY }}>
                    ${jobTotal.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </span>
                </div>

                {/* Platform Fee */}
                <div className="flex items-center justify-between">
                  <span style={{ fontSize: 13.5, fontWeight: 500, color: 'rgba(255,255,255,.6)' }}>Platform Fee (5%)</span>
                  <span style={{ fontSize: 13.5, fontWeight: 700, color: '#67C6B4' }}>
                    ${platformFee.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </span>
                </div>

                <div style={{ borderTop: '1px solid rgba(103,198,180,.2)', paddingTop: 12 }}>
                  {/* Total Due on Acceptance */}
                  <div style={{ padding: 16, borderRadius: 12, marginBottom: 12, background: 'rgba(103,198,180,.08)' }}>
                    <div className="flex items-start justify-between">
                      <div>
                        <p style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.4px', color: '#67C6B4', margin: 0 }}>
                          Due on Acceptance
                        </p>
                        <p style={{ fontSize: 11, marginTop: 2, color: 'rgba(255,255,255,.45)' }}>
                          {isWithin14Days ? 'Full job total + platform fee' : '50% of job total + platform fee'}
                        </p>
                      </div>
                      <p style={{ fontSize: 24, fontWeight: 800, color: '#67C6B4', fontFamily: FONT_DISPLAY, margin: 0 }}>
                        ${totalDueOnAcceptance.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </p>
                    </div>
                  </div>

                  {/* Total Due 14 Days Before OR Full Payment Notice */}
                  {isWithin14Days ? (
                    <div style={{ padding: 12, borderRadius: 12, background: 'rgba(180,83,9,.22)', border: '1px solid rgba(252,211,77,.25)' }}>
                      <div className="flex items-start gap-2.5">
                        <AlertCircle className="w-4 h-4 flex-shrink-0" style={{ color: '#FCD34D', marginTop: 2 }} />
                        <div>
                          <p style={{ fontSize: 12, fontWeight: 700, color: '#FCD34D', margin: 0 }}>
                            Full Payment Required
                          </p>
                          <p style={{ fontSize: 11, marginTop: 2, color: 'rgba(252,211,77,.8)' }}>
                            Job is within 14 days. Full amount charged upon acceptance.
                          </p>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div style={{ padding: 16, borderRadius: 12, background: 'rgba(255,255,255,.04)', border: '1px solid rgba(255,255,255,.08)' }}>
                      <div className="flex items-start justify-between">
                        <div>
                          <p style={{ fontSize: 12, fontWeight: 500, color: 'rgba(255,255,255,.6)', margin: 0 }}>
                            Due on {fourteenDaysBeforeJob.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                          </p>
                          <p style={{ fontSize: 11, marginTop: 2, color: 'rgba(255,255,255,.45)' }}>
                            Remaining 50% — 14 days before job
                          </p>
                        </div>
                        <p style={{ fontSize: 17, fontWeight: 700, color: '#fff', fontFamily: FONT_DISPLAY, margin: 0 }}>
                          ${totalDue14DaysBefore.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Payment Method */}
            <div style={sectionCard}>
              <div style={sectionHeader}>
                <h3 style={sectionLabel}>Payment Method</h3>
              </div>
              <div style={{ padding: 20 }}>
                {loadingCards ? (
                  <div className="space-y-3">
                    {[1, 2].map((i) => (
                      <div key={i} className="flex items-center gap-4" style={{ padding: 16, borderRadius: 12, border: '1px solid rgba(255,255,255,.08)' }}>
                        <div style={{ width: 40, height: 28, borderRadius: 6, background: 'rgba(255,255,255,.08)' }} />
                        <div className="flex-1 space-y-2">
                          <div style={{ height: 16, width: 128, borderRadius: 6, background: 'rgba(255,255,255,.08)' }} />
                          <div style={{ height: 12, width: 80, borderRadius: 6, background: 'rgba(255,255,255,.06)' }} />
                        </div>
                        <div style={{ width: 20, height: 20, borderRadius: 999, background: 'rgba(255,255,255,.08)' }} />
                      </div>
                    ))}
                  </div>
                ) : paymentMethods.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '24px 0' }}>
                    <CreditCard className="w-10 h-10 mx-auto" style={{ color: 'rgba(255,255,255,.35)', marginBottom: 12 }} />
                    <p style={{ fontSize: 13.5, fontWeight: 500, marginBottom: 12, color: 'rgba(255,255,255,.6)' }}>
                      No payment methods on file
                    </p>
                    <button
                      onClick={() => setShowAddCardModal(true)}
                      style={{
                        padding: '10px 16px',
                        borderRadius: 11,
                        border: '2px dashed rgba(255,255,255,.2)',
                        fontSize: 13.5,
                        fontWeight: 600,
                        background: 'transparent',
                        color: 'rgba(255,255,255,.7)',
                        cursor: 'pointer',
                      }}
                    >
                      + Add Payment Method
                    </button>
                  </div>
                ) : (
                  <div className="space-y-2.5">
                    {paymentMethods.map((method) => {
                      const isSelected = selectedPaymentMethod === method.id;
                      return (
                        <label
                          key={method.id}
                          className="flex items-center gap-3"
                          style={{
                            padding: 14,
                            borderRadius: 12,
                            cursor: 'pointer',
                            border: isSelected ? '1px solid rgba(40,192,208,.5)' : '1px solid rgba(255,255,255,.1)',
                            background: isSelected ? 'rgba(40,192,208,.1)' : 'rgba(255,255,255,.02)',
                            boxShadow: isSelected ? '0 0 0 3px rgba(40,192,208,.12)' : 'none',
                          }}
                        >
                          {/* Custom radio indicator */}
                          <input
                            type="radio"
                            name="paymentMethod"
                            value={method.id}
                            checked={isSelected}
                            onChange={(e) => setSelectedPaymentMethod(e.target.value)}
                            className="sr-only"
                          />
                          <div style={{
                            width: 18,
                            height: 18,
                            borderRadius: 999,
                            border: isSelected ? '2px solid #28C0D0' : '2px solid rgba(255,255,255,.3)',
                            background: isSelected ? '#28C0D0' : 'transparent',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexShrink: 0,
                          }}>
                            {isSelected && <div style={{ width: 6, height: 6, borderRadius: 999, background: '#0C1120' }}></div>}
                          </div>
                          {/* Card brand box */}
                          <div style={{
                            width: 40,
                            height: 28,
                            borderRadius: 6,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: 10,
                            fontWeight: 700,
                            textTransform: 'uppercase',
                            background: 'rgba(255,255,255,.06)',
                            color: 'rgba(255,255,255,.5)',
                          }}>
                            {method.brand?.slice(0, 4) || 'CARD'}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p style={{ fontSize: 13.5, fontWeight: 700, color: '#fff', margin: 0, fontFamily: FONT_MONO }}>
                              •••• {method.last4}
                            </p>
                            <p style={{ fontSize: 11, color: 'rgba(255,255,255,.45)', margin: 0, fontFamily: FONT_MONO }}>
                              Expires {method.exp_month}/{method.exp_year}
                            </p>
                          </div>
                          {isSelected && (
                            <CheckCircle className="w-[18px] h-[18px] flex-shrink-0" style={{ color: '#28C0D0' }} />
                          )}
                        </label>
                      );
                    })}
                    <button
                      onClick={() => setShowAddCardModal(true)}
                      style={{
                        width: '100%',
                        padding: 12,
                        borderRadius: 12,
                        border: '2px dashed rgba(255,255,255,.2)',
                        fontSize: 13.5,
                        fontWeight: 600,
                        background: 'transparent',
                        color: 'rgba(255,255,255,.7)',
                        cursor: 'pointer',
                      }}
                    >
                      + Add New Card
                    </button>
                  </div>
                )}

                <div style={{ marginTop: 16, padding: 12, borderRadius: 12, background: 'rgba(255,255,255,.04)' }}>
                  <p style={{ fontSize: 11.5, color: 'rgba(255,255,255,.5)', margin: 0 }}>
                    <Shield className="w-3.5 h-3.5 inline mr-1 -mt-0.5" />
                    Nothing will be charged until the crew member accepts the offer.
                  </p>
                </div>
              </div>
            </div>

            {/* Terms Acceptance */}
            <label
              className="flex items-start gap-3"
              style={{
                padding: 16,
                borderRadius: 16,
                cursor: 'pointer',
                background: acceptedTerms ? 'rgba(40,192,208,.08)' : 'transparent',
                border: acceptedTerms ? '1px solid rgba(40,192,208,.3)' : '1px solid rgba(255,255,255,.1)',
              }}
            >
              <input
                type="checkbox"
                checked={acceptedTerms}
                onChange={(e) => setAcceptedTerms(e.target.checked)}
                style={{ marginTop: 2, width: 18, height: 18, accentColor: '#28C0D0', flexShrink: 0 }}
              />
              <p style={{ fontSize: 13.5, fontWeight: 500, color: '#fff', margin: 0 }}>
                I accept the{' '}
                <a href="/terms" target="_blank" rel="noreferrer" style={{ color: '#28C0D0', fontWeight: 700 }}>
                  Shootzu Terms and Conditions
                </a>
                {' '}and{' '}
                <a href="/crew-contract" target="_blank" rel="noreferrer" style={{ color: '#28C0D0', fontWeight: 700 }}>
                  Crew Contract
                </a>
              </p>
            </label>
          </div>
        </div>

        {/* Footer */}
        <div
          className="p-4 lg:p-6 flex gap-3 lg:gap-4 flex-shrink-0"
          style={{ borderTop: '1px solid rgba(255,255,255,.08)', background: 'rgba(8,11,20,.4)' }}
        >
          <button
            onClick={onClose}
            disabled={loading}
            style={{
              flex: 1,
              height: 52,
              borderRadius: 12,
              fontSize: 14,
              fontWeight: 700,
              background: 'transparent',
              border: '1px solid rgba(255,255,255,.2)',
              color: '#fff',
              cursor: loading ? 'not-allowed' : 'pointer',
              opacity: loading ? 0.6 : 1,
            }}
          >
            Cancel
          </button>
          <button
            onClick={handleSendOffer}
            disabled={loading || !acceptedTerms || (paymentMethods.length > 0 && !selectedPaymentMethod)}
            style={{
              flex: 1.4,
              height: 52,
              borderRadius: 12,
              fontSize: 14,
              fontWeight: 700,
              background: '#28C0D0',
              border: 'none',
              color: '#0C1120',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 12px 28px -10px rgba(40,192,208,.6)',
              cursor: (loading || !acceptedTerms || (paymentMethods.length > 0 && !selectedPaymentMethod)) ? 'not-allowed' : 'pointer',
              opacity: (loading || !acceptedTerms || (paymentMethods.length > 0 && !selectedPaymentMethod)) ? 0.5 : 1,
            }}
          >
            {loading ? (
              <>
                <div style={{ width: 18, height: 18, border: '2px solid rgba(12,17,32,.3)', borderTopColor: '#0C1120', borderRadius: 999, marginRight: 8 }} className="animate-spin"></div>
                <span className="hidden sm:inline">Sending Offer...</span>
                <span className="sm:hidden">Sending...</span>
              </>
            ) : (
              <>
                <CheckCircle className="w-[18px] h-[18px] mr-2" />
                <span className="hidden sm:inline">Send Offer</span>
                <span className="sm:hidden">Send</span>
              </>
            )}
          </button>
        </div>
      </div>

      {/* Add Card Modal - Nested inside Send Offer Modal */}
      {showAddCardModal && (
        <AddCardModal
          isOpen={showAddCardModal}
          onClose={() => setShowAddCardModal(false)}
          onSuccess={handleCardAdded}
          isDark={isDark}
        />
      )}

      {/* Offer Sent Success Modal */}
      {showSuccessModal && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center"
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setShowSuccessModal(false);
              onClose();
            }
          }}
          style={{ background: 'rgba(8,11,20,.8)', backdropFilter: 'blur(6px)', WebkitBackdropFilter: 'blur(6px)', padding: 16, fontFamily: FONT_BODY }}
        >
          <div
            className="w-full max-w-md"
            style={{
              background: '#12162A',
              border: '1px solid rgba(255,255,255,.12)',
              borderRadius: 22,
              boxShadow: '0 40px 90px -30px rgba(0,0,0,.85)',
              overflow: 'hidden',
            }}
          >
            {/* Success Header */}
            <div style={{ position: 'relative', padding: 26, textAlign: 'center', background: 'linear-gradient(120deg,#15803D,#0E7490)', overflow: 'hidden' }}>
              <div style={{ position: 'relative', zIndex: 10 }}>
                {/* Animated ping rings */}
                <div style={{ position: 'relative', width: 64, height: 64, margin: '0 auto 12px' }}>
                  <div className="animate-ping" style={{ position: 'absolute', inset: 0, background: 'rgba(255,255,255,.1)', borderRadius: 999 }}></div>
                  <div className="animate-ping" style={{ position: 'absolute', inset: 4, background: 'rgba(255,255,255,.1)', borderRadius: 999, animationDelay: '0.3s' }}></div>
                  <div style={{ position: 'relative', width: 64, height: 64, background: 'rgba(255,255,255,.2)', borderRadius: 999, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Send className="w-7 h-7" style={{ color: '#fff' }} />
                  </div>
                </div>
                <h2 style={{ fontFamily: FONT_DISPLAY, fontWeight: 800, fontSize: 24, color: '#fff', margin: 0, marginBottom: 4, letterSpacing: '-.4px' }}>
                  Offer Sent!
                </h2>
                <p style={{ color: 'rgba(255,255,255,.85)', fontSize: 13.5, margin: 0 }}>
                  Your job offer has been sent successfully
                </p>
              </div>
            </div>

            {/* Content */}
            <div style={{ padding: 20 }} className="space-y-4">
              {/* Recipient Info */}
              <div style={{ padding: 14, borderRadius: 16, background: 'rgba(103,198,180,.08)', border: '1px solid rgba(103,198,180,.22)' }}>
                <div className="flex items-center gap-3">
                  <div style={{ width: 40, height: 40, borderRadius: 12, background: 'linear-gradient(135deg,#15803D,#0E7490)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontWeight: 700, fontSize: 14, flexShrink: 0 }}>
                    {offerData?.applicant_name?.charAt(0) || 'U'}
                  </div>
                  <div className="min-w-0">
                    <p style={{ fontWeight: 700, fontSize: 13.5, color: '#fff', margin: 0 }}>
                      {offerData?.applicant_name}
                    </p>
                    <p className="truncate" style={{ fontSize: 11.5, color: 'rgba(255,255,255,.6)', margin: 0 }}>
                      {offerData?.applicant_email}
                    </p>
                  </div>
                </div>
              </div>

              {/* 48 Hour Notice */}
              <div style={{ padding: 16, borderRadius: 16, background: 'rgba(180,83,9,.22)', border: '1px solid rgba(252,211,77,.25)' }}>
                <div className="flex items-start gap-2.5">
                  <div style={{ width: 32, height: 32, borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, background: 'rgba(252,211,77,.18)' }}>
                    <Clock className="w-4 h-4" style={{ color: '#FCD34D' }} />
                  </div>
                  <div>
                    <p style={{ fontWeight: 700, fontSize: 13.5, marginTop: 0, marginBottom: 2, color: '#FCD34D' }}>
                      48 Hours to Respond
                    </p>
                    <p style={{ fontSize: 11.5, lineHeight: 1.6, color: 'rgba(252,211,77,.85)', margin: 0 }}>
                      The applicant has <strong>48 hours</strong> to accept or decline. You'll receive an email notification when they respond.
                    </p>
                  </div>
                </div>
              </div>

              {/* What Happens Next */}
              <div style={{ padding: 16, borderRadius: 12, background: 'rgba(255,255,255,.04)', border: '1px solid rgba(255,255,255,.08)' }}>
                <p style={{ fontWeight: 700, fontSize: 11, textTransform: 'uppercase', letterSpacing: '.6px', marginTop: 0, marginBottom: 10, color: 'rgba(255,255,255,.45)' }}>
                  What's next
                </p>
                <div className="space-y-2">
                  {[
                    { icon: <CheckCircle className="w-3.5 h-3.5" style={{ color: '#67C6B4' }} />, text: 'If accepted, payment will be authorized from your card' },
                    { icon: <CheckCircle className="w-3.5 h-3.5" style={{ color: '#67C6B4' }} />, text: 'The job will be confirmed and both parties notified' },
                    { icon: <AlertCircle className="w-3.5 h-3.5" style={{ color: '#FCD34D' }} />, text: 'If no response in 48 hours, the offer expires automatically' },
                  ].map((item, i) => (
                    <div key={i} className="flex items-start gap-2">
                      <span className="flex-shrink-0" style={{ marginTop: 2 }}>{item.icon}</span>
                      <span style={{ fontSize: 11.5, lineHeight: 1.6, color: 'rgba(255,255,255,.75)' }}>{item.text}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Footer */}
            <div style={{ padding: '0 20px 20px' }}>
              <button
                onClick={() => {
                  setShowSuccessModal(false);
                  onClose();
                }}
                style={{
                  width: '100%',
                  height: 48,
                  borderRadius: 12,
                  fontSize: 14,
                  fontWeight: 700,
                  background: '#28C0D0',
                  border: 'none',
                  color: '#0C1120',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  boxShadow: '0 12px 28px -10px rgba(40,192,208,.6)',
                }}
              >
                <CheckCircle className="w-4 h-4 mr-2" />
                Done
              </button>
            </div>
          </div>
        </div>
      )}
      <p className="text-[8px] text-center py-1 opacity-20 select-none" style={{ color: '#fff' }}>modal:send-offer</p>
    </div>
  );
};

export default SendOfferModal;
